import { useState } from 'react';
import { Phone, MessageCircle, Calendar, MapPin, Car, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { format } from 'date-fns';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    carLastFourDigit: '',
    carType: '',
    serviceType: '',
    date: null,
    time: '',
  });

  const [date, setDate] = useState(null);

  const carTypes = ['Hatchback', 'Sedan', 'SUV / Luxury'];
  const serviceTypes = [
    'Regular Service (₹350-450)',
    'Quick Exterior Rinse (₹250-350)',
    'Monthly Subscription',
    'VIP Membership',
  ];
  const timeSlots = [
    '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
    '5:00 PM', '6:00 PM', '7:00 PM',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    setFormData((prev) => ({ ...prev, date: selectedDate }));
  };

  const generateWhatsAppMessage = () => {
    const dateStr = formData.date ? format(formData.date, 'PPP') : 'Not selected';
    return encodeURIComponent(
      `Hi Washora, I'd like to book a car wash service.\n\n` +
      `📋 *Booking Details*\n` +
      `👤 Name: ${formData.name || 'Not provided'}\n` +
      `📞 Phone: ${formData.phone || 'Not provided'}\n` +
      `📍 Location: ${formData.location || 'Not provided'}\n` +
      `🔢 Car No. (Last 4): ${formData.carLastFourDigit || 'Not provided'}\n` +
      `🚗 Car Type: ${formData.carType || 'Not selected'}\n` +
      `🧼 Service: ${formData.serviceType || 'Not selected'}\n` +
      `📅 Date: ${dateStr}\n` +
      `⏰ Time: ${formData.time || 'Not selected'}`
    );
  };

  const handleWhatsAppBooking = () => {
    const message = generateWhatsAppMessage();
    window.open(`https://wa.me/919044499957?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20" data-testid="booking-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            <Calendar className="h-4 w-4 mr-2" />
            Book Your Service
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Book Your Doorstep Wash
          </h1>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            Fill in your details and we'll come to you. It's that simple!
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
                <h2 className="font-heading text-2xl font-bold text-[#0B2C5D] mb-8">
                  Booking Details
                </h2>
                
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-[#0B2C5D] font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                      className="mt-2 bg-slate-50 border-slate-200 focus:border-[#1E88E5] focus:ring-blue-100"
                      data-testid="booking-name"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone" className="text-[#0B2C5D] font-medium">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="mt-2 bg-slate-50 border-slate-200 focus:border-[#1E88E5] focus:ring-blue-100"
                      data-testid="booking-phone"
                    />
                  </div>

                  {/* Location & Car Last 4 Digit */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="location" className="text-[#0B2C5D] font-medium">
                        Service Location *
                      </Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="Enter your address"
                        className="mt-2 bg-slate-50 border-slate-200 focus:border-[#1E88E5] focus:ring-blue-100"
                        data-testid="booking-location"
                      />
                    </div>

                    <div>
                      <Label htmlFor="carLastFourDigit" className="text-[#0B2C5D] font-medium">
                        Car Last 4 Digits *
                      </Label>
                      <Input
                        id="carLastFourDigit"
                        name="carLastFourDigit"
                        value={formData.carLastFourDigit}
                        onChange={handleInputChange}
                        placeholder="e.g., 1234"
                        maxLength={4}
                        className="mt-2 bg-slate-50 border-slate-200 focus:border-[#1E88E5] focus:ring-blue-100"
                        data-testid="booking-car-digits"
                      />
                    </div>
                  </div>

                  {/* Car Type & Service Type */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-[#0B2C5D] font-medium">Car Type *</Label>
                      <Select onValueChange={(value) => handleSelectChange('carType', value)}>
                        <SelectTrigger className="mt-2 bg-slate-50 border-slate-200" data-testid="booking-car-type">
                          <SelectValue placeholder="Select car type" />
                        </SelectTrigger>
                        <SelectContent>
                          {carTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-[#0B2C5D] font-medium">Service Type *</Label>
                      <Select onValueChange={(value) => handleSelectChange('serviceType', value)}>
                        <SelectTrigger className="mt-2 bg-slate-50 border-slate-200" data-testid="booking-service-type">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-[#0B2C5D] font-medium">Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full mt-2 justify-start text-left font-normal bg-slate-50 border-slate-200 hover:bg-slate-100"
                            data-testid="booking-date"
                          >
                            <Calendar className="mr-2 h-4 w-4 text-[#1E88E5]" />
                            {date ? format(date, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={date}
                            onSelect={handleDateChange}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <Label className="text-[#0B2C5D] font-medium">Preferred Time *</Label>
                      <Select onValueChange={(value) => handleSelectChange('time', value)}>
                        <SelectTrigger className="mt-2 bg-slate-50 border-slate-200" data-testid="booking-time">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="pt-6 space-y-4">
                    <Button
                      onClick={handleWhatsAppBooking}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg rounded-full font-semibold transition-all transform hover:-translate-y-0.5 shadow-lg"
                      data-testid="booking-whatsapp-submit"
                    >
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Book via WhatsApp
                    </Button>

                    <a
                      href="tel:9044499957"
                      className="flex items-center justify-center w-full bg-[#0B2C5D] hover:bg-[#0a2651] text-white py-4 text-lg rounded-full font-semibold transition-all"
                      data-testid="booking-call"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Or Call: 9044499957
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Contact */}
              <div className="bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D] rounded-2xl p-8 text-white">
                <h3 className="font-heading text-xl font-bold mb-6">Quick Contact</h3>
                <div className="space-y-4">
                  <a href="tel:9044499957" className="flex items-center hover:text-[#F7931E] transition-colors">
                    <Phone className="h-5 w-5 mr-3" />
                    9044499957
                  </a>
                  <a 
                    href="https://wa.me/919044499957" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center hover:text-[#F7931E] transition-colors"
                  >
                    <MessageCircle className="h-5 w-5 mr-3" />
                    WhatsApp Us
                  </a>
                </div>
              </div>

              {/* Service Hours */}
              <div className="bg-[#F8FAFC] rounded-2xl p-8">
                <div className="flex items-center mb-4">
                  <Clock className="h-6 w-6 text-[#1E88E5] mr-3" />
                  <h3 className="font-heading text-xl font-bold text-[#0B2C5D]">Service Hours</h3>
                </div>
                <p className="text-[#64748B]">
                  7:00 AM - 7:00 PM<br />
                  <span className="text-[#F7931E] font-medium">All Days</span>
                </p>
              </div>

              {/* Service Area */}
              <div className="bg-[#F8FAFC] rounded-2xl p-8">
                <div className="flex items-center mb-4">
                  <MapPin className="h-6 w-6 text-[#1E88E5] mr-3" />
                  <h3 className="font-heading text-xl font-bold text-[#0B2C5D]">Service Area</h3>
                </div>
                <p className="text-[#64748B]">
                  Chitradurga & surrounding areas<br />
                  <span className="text-sm">#3/21, 3rd Cross, Basaweshwara Nagar - 577502</span>
                </p>
              </div>

              {/* Why Book */}
              <div className="bg-white rounded-2xl border border-slate-100 p-8">
                <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-4">Why Book With Us?</h3>
                <ul className="space-y-3">
                  {[
                    'Doorstep convenience',
                    'Premium quality products',
                    'Trained professionals',
                    'Transparent pricing',
                    'VIP membership available',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center text-[#64748B]">
                      <svg className="w-5 h-5 text-[#F7931E] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
